import React, { useState, useEffect } from "react";
import { Button, Select, Tag, Alert, message } from "antd";
import { CheckCircleOutlined, WarningOutlined, RobotOutlined } from "@ant-design/icons";
import { getCodeTables } from "../../../service/gateway.service";
import { NAVY, BLUE, GREEN, RED, TRANSFORMS } from "../gateway.constants";

const { Option } = Select;

function ColumnMapper({
  fields, sourceColumns, mappings, savedMappings, systemId,
  hasRepeatingGroups, groupByColumn, onGroupByColumnChange,
  onChange, onLoadSaved, onNext, onBack, preflightLoading,
}) {
  const [codeTables, setCodeTables] = useState([]);
  const [savedSel,   setSavedSel]   = useState(null);

  useEffect(() => {
    if (systemId) getCodeTables(systemId).then((r) => setCodeTables(r.data || []));
  }, [systemId]);

  const update = (tf, patch) =>
    onChange(mappings.map((m) => (m.targetField === tf ? { ...m, ...patch } : m)));

  const mandatoryUnmapped = fields
    .filter((f) => f.isMandatory && !f.defaultValue && f.dataType !== "EXPR")
    .filter((f) => !mappings.find((m) => m.targetField === f.fieldName && m.sourceColumn));

  const mappedCount = mappings.filter((m) => m.sourceColumn).length;

  const ctByField = {};
  codeTables.forEach((ct) => {
    if (!ctByField[ct.field_name]) ctByField[ct.field_name] = [];
    ctByField[ct.field_name].push(ct);
  });

  return (
    <div>
      {/* Summary bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
        borderRadius: 8, marginBottom: 16,
        background: "var(--color-background-secondary)",
        border: "0.5px solid var(--color-border-tertiary)",
      }}>
        <RobotOutlined style={{ color: BLUE }} />
        <span style={{ fontSize: 13, flex: 1 }}>
          {mappedCount}/{fields.length} auto-mapped ·{" "}
          {mandatoryUnmapped.length > 0
            ? <span style={{ color: RED }}>{mandatoryUnmapped.length} mandatory still need mapping</span>
            : <span style={{ color: GREEN }}>All mandatory fields mapped ✓</span>}
        </span>
        {savedMappings.length > 0 && (
          <Select size="small" placeholder="Load saved" style={{ width: 180 }}
            value={savedSel}
            onChange={(id) => {
              const s = savedMappings.find((m) => m.mappingId === id);
              if (s) { onLoadSaved(s); setSavedSel(id); message.success("Mapping loaded"); }
            }}>
            {savedMappings.map((m) => (
              <Option key={m.mappingId} value={m.mappingId}>{m.mappingName}</Option>
            ))}
          </Select>
        )}
      </div>

      {/* Repeating group config */}
      {hasRepeatingGroups && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="Repeating group detected"
          description={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span>Group rows by:</span>
              <Select size="small" style={{ width: 180 }}
                value={groupByColumn || undefined}
                onChange={onGroupByColumnChange} placeholder="key column">
                {sourceColumns.map((c) => <Option key={c} value={c}>{c}</Option>)}
              </Select>
            </div>
          }
        />
      )}

      {/* Column header row */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 20px 1fr 130px 140px 28px",
        gap: 8, padding: "5px 0",
        borderBottom: "0.5px solid var(--color-border-tertiary)", marginBottom: 4,
      }}>
        {["Target Field", "", "Source Column", "Transform", "Code Table", ""].map((h, i) => (
          <div key={i} style={{
            fontSize: 10, fontWeight: 600,
            color: "var(--color-text-secondary)",
            textTransform: "uppercase", letterSpacing: ".5px",
          }}>
            {h}
          </div>
        ))}
      </div>

      {/* Field rows */}
      {fields.map((f) => {
        const m      = mappings.find((x) => x.targetField === f.fieldName) || {};
        const isMapped = !!m.sourceColumn || !!f.defaultValue || f.dataType === "EXPR";
        const tbls   = ctByField[f.fieldName] || [];
        return (
          <div key={f.fieldId || f.fieldName} style={{
            display: "grid", gridTemplateColumns: "1fr 20px 1fr 130px 140px 28px",
            gap: 8, alignItems: "center", padding: "5px 0",
            borderBottom: "0.5px solid var(--color-border-tertiary)",
            background: f.isMandatory && !isMapped ? "#fff9f9" : "transparent",
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, display: "flex", gap: 4 }}>
                {f.displayName}
                {f.isMandatory && <span style={{ color: RED, fontSize: 11 }}>*</span>}
                {f.dataType === "EXPR" && <Tag color="purple" style={{ fontSize: 9, padding: "0 4px" }}>EXPR</Tag>}
              </div>
              <div style={{ fontSize: 10, color: "var(--color-text-secondary)" }}>
                {f.dataType}
                {f.dateFormat  ? ` · ${f.dateFormat}`   : ""}
                {f.defaultValue ? ` · default: ${f.defaultValue}` : ""}
              </div>
            </div>

            <div style={{ color: "var(--color-text-secondary)", textAlign: "center", fontSize: 11 }}>→</div>

            <Select showSearch allowClear size="small" style={{ width: "100%" }}
              placeholder={f.dataType === "EXPR" ? "(computed)" : "select column"}
              value={m.sourceColumn || undefined}
              disabled={f.dataType === "EXPR"}
              filterOption={(inp, opt) => opt?.value?.toLowerCase().includes(inp.toLowerCase())}
              onChange={(val) => update(f.fieldName, { sourceColumn: val || null })}>
              {sourceColumns.map((c) => <Option key={c} value={c}>{c}</Option>)}
            </Select>

            <Select size="small" style={{ width: "100%" }}
              value={m.transform || null}
              disabled={!m.sourceColumn || f.dataType === "EXPR"}
              onChange={(val) => update(f.fieldName, { transform: val })}>
              {TRANSFORMS.map((t) => (
                <Option key={t.value || "none"} value={t.value}>{t.label}</Option>
              ))}
            </Select>

            <Select size="small" allowClear style={{ width: "100%" }}
              placeholder={tbls.length > 0 ? "translate values" : "no tables"}
              disabled={tbls.length === 0 || !m.sourceColumn}
              value={m.codeTableId || undefined}
              onChange={(val) => update(f.fieldName, { codeTableId: val || null })}>
              {[...new Set(tbls.map((t) => t.code_table_id))].map((id) => (
                <Option key={id} value={id}>{id}</Option>
              ))}
            </Select>

            {isMapped
              ? <CheckCircleOutlined style={{ color: GREEN, fontSize: 13 }} />
              : f.isMandatory
                ? <WarningOutlined style={{ color: RED, fontSize: 13 }} />
                : <span style={{ color: "#ccc" }}>—</span>}
          </div>
        );
      })}

      <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
        <Button onClick={onBack}>Back</Button>
        <Button type="primary" loading={preflightLoading}
          style={{ background: NAVY, borderColor: NAVY }}
          disabled={mandatoryUnmapped.length > 0}
          onClick={onNext}>
          Run pre-flight check →
        </Button>
        {mandatoryUnmapped.length > 0 && (
          <span style={{ fontSize: 12, color: RED, alignSelf: "center" }}>
            {mandatoryUnmapped.length} field{mandatoryUnmapped.length > 1 ? "s" : ""} unmapped
          </span>
        )}
      </div>
    </div>
  );
}

export default ColumnMapper;
